import { useEffect, useMemo, useRef, useState } from 'react'
import type { ExtractionStage } from '../../../api/listeners/document-extraction'
import { Button } from '../../ui/button'
import { ListenerNames, RecognizedNames, recognizedNamePackets } from './recognized-names'
import css from './ui.recognition.module.scss'

type Props = {
    stage: ExtractionStage | null
    names: ListenerNames
    onNames: (value: RecognizedNames, expectedNames: ListenerNames) => boolean
}

export const RecognizedNamesTransfer = ({ stage, names, onNames }: Props) => {
    const baseline = useRef({ ...names })
    const apply = useRef(onNames)
    apply.current = onNames
    const { packets, needsReview } = useMemo(() => recognizedNamePackets(stage), [stage])
    const [selected, setSelected] = useState<number | null>(null)
    const [state, setState] = useState({ applied: false, message: '', name: '' })
    const attempted = useRef('')
    const index = selected ?? (packets.length === 1 && !needsReview ? 0 : null)
    const packet = index === null ? undefined : packets[index]

    useEffect(() => {
        if (!packet) return
        const key = JSON.stringify([stage?.stageID, packet])
        if (attempted.current === key) return
        attempted.current = key
        if (!apply.current(packet, baseline.current)) {
            setState({ applied: false, message: 'ФИО не перенесены: анкета или ФИО уже изменены. Проверьте поля в «Основных данных».', name: '' })
            return
        }
        setState({ applied: true, message: 'Распознанные поля ФИО перенесены в «Основные данные» и сохранятся вместе с анкетой.', name: [packet.lastName, packet.firstName, packet.secondName].filter(Boolean).join(' ') })
    }, [packet, stage?.stageID])

    if (!packets.length && !needsReview) return null
    return <section className={css.group} aria-label="Перенос ФИО">
        <h5>Фамилия, имя, отчество</h5>
        {index === null ? <>
            <p className={css.warning}>{packets.length
                ? 'В паспортах разные ФИО или есть неоднозначный результат. Проверьте оригинал и выберите данные одного паспорта.'
                : 'ФИО требуют проверки. Уточните написание по оригиналу.'}</p>
            {packets.map((value, itemIndex) => <p key={JSON.stringify(value)}><Button onClick={() => setSelected(itemIndex)}>
                Перенести ФИО: {[value.lastName, value.firstName, value.secondName].filter(Boolean).join(' ')}
            </Button></p>)}
        </> : <>
            <p role="status" aria-live="polite" className={state.applied ? undefined : css.warning}>{state.message}</p>
            {state.name && <p>{state.name}</p>}
        </>}
    </section>
}
